import axios from 'axios'
import { bootGate } from '@/utils/gate'
import { buildServiceRejection } from './serviceError'
import {
	claimMissingRouteReload,
	isMissingRouteResponse,
} from '@/utils/missingRouteReload'

// 建立一個標誌，用於追蹤是否已經處理過 401 錯誤
let isHandling401 = false
let storeInstance = null

const getStore = () => {
	if (storeInstance) {
		return storeInstance
	}
	try {
		// eslint-disable-next-line global-require
		const resolved = require('@/store')
		storeInstance = resolved.default || resolved
	} catch (error) {
		console.warn('service: 無法取得 store 實例', error)
	}
	return storeInstance
}

const service = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_API}/api`,
	timeout: 60000,
})

service.interceptors.request.use(
	async config => {
		await bootGate.whenReady('axios.js')

		// 如果是 FormData，不要覆蓋 Content-Type，讓瀏覽器自動處理
		if (!(config.data instanceof FormData)) {
			config.headers['Content-Type'] = 'application/json'
		}

		let storageState = null

		const store = getStore()
		if (store?.state?.storage) {
			storageState = store.state.storage
		}

		if (!storageState || (!storageState.authToken && localStorage.adminVuex)) {
			try {
				const persisted = JSON.parse(localStorage.adminVuex)
				storageState = persisted.storage || null
				console.warn('進到舊式兼容寫法')
			} catch (error) {
				console.warn('service: 解析 localStorage adminVuex 失敗', error)
			}
		}

		if (storageState) {
			const authToken = storageState.authToken
			if (authToken) {
				config.headers['X-Auth-Token'] = authToken
			}
			config.headers['X-Back-Stage-Version'] = process.env.VUE_APP_VERSION

			const xSid = storageState.storeId
			if (xSid) {
				config.headers['X-SID'] = xSid
			}

			const androidVersion = storageState.androidVersion
			if (androidVersion) {
				config.headers['X-Android-Version'] = androidVersion
			}

			const iosVersion = storageState.iosVersion
			if (iosVersion) {
				config.headers['X-Ios-Version'] = iosVersion
			}
		}
		return config
	},
	error => {
		return Promise.reject(error)
	},
)

service.interceptors.response.use(
	response => {
		const authToken = response.headers['x-auth-token']
		if (authToken) {
			const store = getStore()
			if (store) {
				store.commit('storage/setAuthToken', authToken)
			}
		}
		// 檔案下載若需讀取 Content-Disposition，可由單一端點選擇保留完整回應
		return response.config.returnRawResponse ? response : response.data
	},
	error => {
		if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
			return Promise.reject('網絡問題，請檢查您的網絡連接')
		}
		const res = error.response
		if (!res) {
			return Promise.reject('無法獲取伺服器回應')
		}
		if (
			isMissingRouteResponse(res.config?.reloadOnMissingRoute, res.status) &&
			claimMissingRouteReload(window.sessionStorage, Date.now())
		) {
			window.location.reload()
			// 頁面即將重新整理：不 resolve 也不 reject，呼叫端不會先跳錯誤提示
			return new Promise(() => {})
		}

		const store = getStore()

		if (res.status === 503) {
			const { title, message } = res.data.data
			if (store) {
				store.commit(
					'setMaintenance',
					{
						modal: true,
						title: title,
						message: message,
					},
					{ root: true },
				)
			}
			return Promise.reject('服務更新中')
		} else {
			if (store) {
				store.commit(
					'setMaintenance',
					{
						modal: false,
						title: '',
						message: '',
					},
					{ root: true },
				)
			}
		}

		if (res.status === 401 && res.config.url !== 'logout') {
			if (!store) {
				return Promise.reject('未授權，請重新登入')
			}
			if (!isHandling401) {
				isHandling401 = true
				store.dispatch('logout', false).finally(() => {
					// 重置標誌，允許未來再次處理 401
					// 這一步可能不會執行，因為頁面可能已經重定向
					setTimeout(() => {
						isHandling401 = false
					}, 1000)
				})
			}
			return new Promise(() => {})
		} else if (res.status === 429) {
			return Promise.reject(
				res.data.message ? res.data.message : '操作過於頻繁',
			)
		} else {
			return Promise.reject(buildServiceRejection(res))
		}
	},
)

export default service
